import type { Token, Tokens } from 'marked'

/**
 * Validates that markdown syntax markers are balanced (all opened markers are closed).
 * Returns an object describing any unbalanced markers found.
 *
 * This is used as a post-condition check after closeUnfinishedSyntax to detect
 * any edge cases the closing logic might have missed.
 */
function validateSyntaxBalance(text: string): {
  isBalanced: boolean
  unbalanced: { marker: string; count: number }[]
} {
  if (!text) return { isBalanced: true, unbalanced: [] }

  // Remove code spans first (content inside backticks shouldn't count)
  const noCode = text.replace(/`[^`]*`/g, '')

  const unbalanced: { marker: string; count: number }[] = []

  // Check bold (**)
  const boldMatches = noCode.match(/\*\*/g)
  const boldCount = boldMatches ? boldMatches.length : 0
  if (boldCount % 2 === 1) {
    unbalanced.push({ marker: '**', count: boldCount })
  }

  // Check italic (*) - remove ** first
  const noBold = noCode.replace(/\*\*/g, '')
  // Also remove list markers (* at start of line with space)
  const noBoldNoList = noBold.replace(/^[*] /gm, '  ')
  const italicMatches = noBoldNoList.match(/\*/g)
  const italicCount = italicMatches ? italicMatches.length : 0
  if (italicCount % 2 === 1) {
    unbalanced.push({ marker: '*', count: italicCount })
  }

  // Check bold underscore (__)
  const boldAltMatches = noCode.match(/__/g)
  const boldAltCount = boldAltMatches ? boldAltMatches.length : 0
  if (boldAltCount % 2 === 1) {
    unbalanced.push({ marker: '__', count: boldAltCount })
  }

  // Check italic underscore (_) - remove __ first
  const noBoldAlt = noCode.replace(/__/g, '')
  const italicAltMatches = noBoldAlt.match(/_/g)
  const italicAltCount = italicAltMatches ? italicAltMatches.length : 0
  if (italicAltCount % 2 === 1) {
    unbalanced.push({ marker: '_', count: italicAltCount })
  }

  // Check strikethrough (~~)
  const strikeMatches = noCode.match(/~~/g)
  const strikeCount = strikeMatches ? strikeMatches.length : 0
  if (strikeCount % 2 === 1) {
    unbalanced.push({ marker: '~~', count: strikeCount })
  }

  // Check backticks
  const backtickMatches = text.match(/`/g)
  const backtickCount = backtickMatches ? backtickMatches.length : 0
  if (backtickCount % 2 === 1) {
    unbalanced.push({ marker: '`', count: backtickCount })
  }

  return {
    isBalanced: unbalanced.length === 0,
    unbalanced,
  }
}

/**
 * Check if there's content after the last occurrence of a marker.
 * Content must be non-whitespace and non-marker characters.
 */
function hasContentAfterMarker(str: string, marker: string): boolean {
  const lastIndex = str.lastIndexOf(marker)
  if (lastIndex === -1) return false
  const afterMarker = str.slice(lastIndex + marker.length)
  return /[^\s*_`~]/.test(afterMarker)
}

/**
 * For multi-character markers (**, __, ~~), check content BEFORE or AFTER.
 * This handles cases like "text.**" where ** at the end starts unclosed bold.
 */
function shouldCloseMultiCharMarker(str: string, marker: string): boolean {
  const lastIndex = str.lastIndexOf(marker)
  if (lastIndex === -1) return false

  const afterMarker = str.slice(lastIndex + marker.length)
  if (/[^\s*_`~]/.test(afterMarker)) return true

  const beforeMarker = str.slice(0, lastIndex)
  if (/[^\s*_`~]/.test(beforeMarker)) return true

  return false
}

/**
 * Force-close any remaining unbalanced markers.
 * This is a fallback for edge cases that closeUnfinishedSyntax might miss.
 *
 * IMPORTANT: Uses the same rules as the main closing logic:
 * - Single-char markers (`, *, _): only close if content AFTER the marker
 * - Multi-char markers (**, __, ~~): close if content BEFORE or AFTER
 */
function forceCloseSyntax(
  text: string,
  unbalanced: { marker: string; count: number }[],
): string {
  let result = text

  // Extract trailing whitespace to insert closers before it
  const trailingWsMatch = result.match(/(\s*)$/)
  const trailingWs = trailingWsMatch ? trailingWsMatch[1] : ''
  if (trailingWs) {
    result = result.slice(0, -trailingWs.length)
  }

  // Close each unbalanced marker (priority: code, bold, italic, etc.)
  // Order matters for proper nesting
  // Only close if the same conditions as the main logic are met
  const closeOrder: Array<{
    marker: string
    shouldClose: (str: string) => boolean
  }> = [
    // Single-char markers: only close if content AFTER
    {
      marker: '`',
      shouldClose: (s) => hasContentAfterMarker(s, '`'),
    },
    // Multi-char markers: close if content BEFORE or AFTER
    {
      marker: '**',
      shouldClose: (s) => shouldCloseMultiCharMarker(s, '**'),
    },
    {
      marker: '__',
      shouldClose: (s) => shouldCloseMultiCharMarker(s, '__'),
    },
    {
      marker: '~~',
      shouldClose: (s) => shouldCloseMultiCharMarker(s, '~~'),
    },
    // Single-char markers: only close if content AFTER
    {
      marker: '*',
      shouldClose: (s) => hasContentAfterMarker(s, '*'),
    },
    {
      marker: '_',
      shouldClose: (s) => hasContentAfterMarker(s, '_'),
    },
  ]

  for (const { marker, shouldClose } of closeOrder) {
    const found = unbalanced.find((u) => u.marker === marker)
    if (found && shouldClose(result)) {
      result += marker
    }
  }

  return result + trailingWs
}

/**
 * Simple, predictable syntax closer for streaming markdown.
 * Unlike lookBack, this is monotonic - output only grows, never shrinks.
 * Just closes unclosed inline syntax at the truncation point.
 *
 * IMPORTANT: This function uses paragraph-level isolation to prevent cascade errors.
 * Markdown inline formatting (bold, italic, etc.) is processed per-paragraph,
 * so unclosed markers in paragraph N cannot affect paragraph N+1.
 *
 * Key insight: Only close syntax if there's content AFTER the opening marker.
 * This prevents weird output when revealing markers character-by-character.
 *
 * Critical fix: In markdown, closing markers must be immediately preceded by
 * non-whitespace. "**bold **" does NOT render as bold! We must insert closing
 * markers BEFORE any trailing whitespace: "**bold** " renders correctly.
 */
export function closeUnfinishedSyntax(text: string): string {
  if (!text) return text

  let result = text

  // FIRST: Handle fenced code blocks at the FULL TEXT level (before paragraph splitting).
  // Fenced code blocks can contain blank lines, so they must NOT be split into paragraphs.
  // Count fences that are at the start of a line (the standard markdown fence pattern).
  const fenceMatches = result.match(/^```/gm)
  const fenceCount = fenceMatches ? fenceMatches.length : 0
  const hasUnclosedFence = fenceCount % 2 === 1

  if (hasUnclosedFence) {
    // Close fenced code block with matching fence on new line.
    // Don't add if we're still typing the opening fence (no newline after it yet).
    const lastFenceIndex = result.lastIndexOf('```')
    const afterFence = result.slice(lastFenceIndex + 3)
    const hasNewlineAfterFence = afterFence.includes('\n')

    if (hasNewlineAfterFence) {
      // There's content after the fence - close it
      result += '\n```'
    }
    // If no newline yet, user is still typing the fence/language - don't close

    // Return early - no inline formatting inside unclosed code fence
    return result
  }

  // If all fences are balanced, we can safely do paragraph-level inline formatting.
  // Split on paragraph breaks (double newlines) to isolate formatting per paragraph.
  // This prevents unclosed markers in one paragraph from affecting subsequent paragraphs.
  // We use a capture group to preserve the separators.
  const segments = result.split(/(\n\n+)/)

  // Track whether we're inside a fenced code block.
  // Segments inside code blocks should NOT be processed for inline formatting.
  let insideCodeFence = false

  return segments
    .map((segment, index) => {
      // Preserve paragraph separators (newlines) as-is
      if (/^\n+$/.test(segment)) return segment

      // Count fences in this segment to update the fence state
      const segmentFences = (segment.match(/^```/gm) || []).length

      // If we're inside a code fence, skip inline formatting
      if (insideCodeFence) {
        // Update state: odd number of fences means we exit the code block
        if (segmentFences % 2 === 1) {
          insideCodeFence = false
        }
        return segment // Don't process content inside code blocks
      }

      // Check if this segment starts a code fence
      if (segmentFences % 2 === 1) {
        // This segment has an opening fence - check if it also closes
        // If odd fences, we're now inside a code block
        insideCodeFence = true
        return segment // Don't process - it's code block content
      }

      // For the last segment, apply full closing logic
      // For earlier segments, they should already be "complete" paragraphs
      const isLastSegment = index === segments.length - 1
      return closeUnfinishedSyntaxForSegment(segment, isLastSegment)
    })
    .join('')
}

/**
 * Close unfinished syntax for a single paragraph/segment.
 * This is the core logic, isolated to prevent cross-paragraph cascade errors.
 */
function closeUnfinishedSyntaxForSegment(
  text: string,
  isLastSegment: boolean,
): string {
  if (!text) return text

  // Extract trailing whitespace (spaces, tabs, newlines) - we'll re-add after closing markers
  // This is critical: "**bold **" won't parse as bold, but "**bold** " will!
  // Similarly, "**bold\n**" won't parse as bold (markers on different lines).
  const trailingWsMatch = text.match(/(\s*)$/)
  const trailingWs = trailingWsMatch ? trailingWsMatch[1] : ''
  let result = trailingWs ? text.slice(0, -trailingWs.length) : text

  // If result is now empty (was all whitespace), just return original
  if (!result) return text

  // FIRST: Close inline syntax (before table handling, so bold/italic close inside cells)

  // For non-last segments (completed paragraphs), we're more aggressive about closing.
  // For the last segment (still streaming), we use conservative logic that checks for content.
  const hasContent = /[^\s*_`~]/.test(result)

  // STEP 1: Handle inline code spans (single backticks)
  // Note: Fenced code blocks (```) are handled at the full-text level in closeUnfinishedSyntax
  // Remove any complete fenced code blocks from backtick count to avoid false positives
  const noFencedCode = result.replace(/```[\s\S]*?```/g, '')
  const backtickMatches = noFencedCode.match(/`/g)
  const backtickCount = backtickMatches ? backtickMatches.length : 0
  const shouldCloseBacktick =
    backtickCount % 2 === 1 &&
    ((!isLastSegment && hasContent) || hasContentAfterMarker(noFencedCode, '`'))
  if (shouldCloseBacktick) {
    result += '`'
  }

  // STEP 2: Now count emphasis markers (after code is closed)
  // Remove code spans and fenced code blocks to avoid counting markers inside code
  const noFencedBlocks = result.replace(/```[\s\S]*?```/g, '')
  const noCode = noFencedBlocks.replace(/`[^`]*`/g, '')

  // Bold (**)
  const boldMatches = noCode.match(/\*\*/g)
  const boldCount = boldMatches ? boldMatches.length : 0
  const shouldCloseBold =
    boldCount % 2 === 1 &&
    ((!isLastSegment && hasContent) || shouldCloseMultiCharMarker(noCode, '**'))

  // Italic (*) - only single asterisks not part of **
  // IMPORTANT: List markers (e.g., "* item" at start of line) should not count as emphasis
  const noCodeNoBold = noCode.replace(/\*\*/g, '')
  const noCodeNoBoldNoList = noCodeNoBold.replace(/^\* /gm, '  ')
  const italicMatches = noCodeNoBoldNoList.match(/\*/g)
  const italicCount = italicMatches ? italicMatches.length : 0
  const shouldCloseItalic =
    italicCount % 2 === 1 &&
    ((!isLastSegment && hasContent) ||
      hasContentAfterMarker(noCodeNoBoldNoList, '*'))

  // Bold alt (__)
  const boldAltMatches = noCode.match(/__/g)
  const boldAltCount = boldAltMatches ? boldAltMatches.length : 0
  const shouldCloseBoldAlt =
    boldAltCount % 2 === 1 &&
    ((!isLastSegment && hasContent) || shouldCloseMultiCharMarker(noCode, '__'))

  // Italic alt (_) - only single underscores not part of __
  const noCodeNoBoldAlt = noCode.replace(/__/g, '')
  const italicAltMatches = noCodeNoBoldAlt.match(/_/g)
  const italicAltCount = italicAltMatches ? italicAltMatches.length : 0
  const shouldCloseItalicAlt =
    italicAltCount % 2 === 1 &&
    ((!isLastSegment && hasContent) ||
      hasContentAfterMarker(noCodeNoBoldAlt, '_'))

  // Strikethrough (~~)
  const strikeMatches = noCode.match(/~~/g)
  const strikeCount = strikeMatches ? strikeMatches.length : 0
  const shouldCloseStrike =
    strikeCount % 2 === 1 &&
    ((!isLastSegment && hasContent) || shouldCloseMultiCharMarker(noCode, '~~'))

  // STEP 3: Close emphasis markers in proper nesting order
  // Inner markers (single-char) close before outer markers (double-char)
  // This ensures **bold *italic closes as **bold *italic*** (italic then bold)

  // Single-char emphasis before double-char (inner before outer)
  if (shouldCloseItalic) {
    result += '*'
  }
  if (shouldCloseBold) {
    result += '**'
  }

  // Same for underscore variants
  if (shouldCloseItalicAlt) {
    result += '_'
  }
  if (shouldCloseBoldAlt) {
    result += '__'
  }

  // Strikethrough last
  if (shouldCloseStrike) {
    result += '~~'
  }

  // SECOND: Handle table row closing (before re-adding trailing whitespace)
  // This ensures "| Cell |" not "| Cell  |" when input is "| Cell "
  {
    const lines = result.split('\n')
    const lastLine = lines[lines.length - 1]

    // Check if we're in a table row (starts with |, doesn't end with |)
    if (lastLine.trim().startsWith('|') && !lastLine.trim().endsWith('|')) {
      result += ' |'
    }
  }

  // Re-add trailing whitespace AFTER inline syntax and table row closing
  // but BEFORE table header detection (which needs newlines)
  result += trailingWs

  // THIRD: Strip incomplete list markers at end of text
  // When streaming, we might have "* " or "- " or "+ " at the start of a line
  // without the actual list item content yet. This can cause a brief bullet flash.
  // Also handle isolated markers without space (like "*\n" or "*" at end).
  // Strip these entirely - they'll reappear when actual content arrives.
  //
  // We need to check the LAST NON-EMPTY line, since trailing whitespace
  // might include newlines that create empty lines at the end.
  {
    const lines = result.split('\n')

    // Find the last non-empty line (or empty-ish line that's just whitespace)
    let lastNonEmptyIdx = lines.length - 1
    while (lastNonEmptyIdx > 0 && lines[lastNonEmptyIdx].trim() === '') {
      lastNonEmptyIdx--
    }
    const lastNonEmptyLine = lines[lastNonEmptyIdx]

    // Check if this line is ONLY a potential list marker (with or without trailing space)
    // This catches: "* ", "*", "- ", "-", "+ ", "+"
    // But NOT valid list items like "* Item" or "* a"
    if (/^[\s]*[*\-+][\s]*$/.test(lastNonEmptyLine)) {
      // Remove the incomplete list marker line and any trailing empty lines
      lines.splice(lastNonEmptyIdx)
      result = lines.join('\n')
    }
  }

  // FOURTH: Optimistic table separator handling
  // Strategy:
  // 1. As soon as we have a complete header row, add separator (even before newline)
  // 2. Separator column count matches header column count (updates as header grows)
  // 3. While LLM types separator row: KEEP our separator (don't let partial break table)
  // 4. Once LLM's separator is complete: let it through
  {
    const lines = result.split('\n')

    // Find first complete table row (potential header)
    const firstTableRowIdx = lines.findIndex((line) => {
      const trimmed = line.trim()
      return (
        trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2
      )
    })

    if (firstTableRowIdx !== -1) {
      const headerLine = lines[firstTableRowIdx]
      const colCount = (headerLine.match(/\|/g) || []).length - 1
      const nextLineRaw = lines[firstTableRowIdx + 1] ?? null
      const nextLine = nextLineRaw?.trim() || ''

      // Check if there's a line after the header (meaning newline was sent)
      const hasNewlineAfterHeader = nextLineRaw !== null

      // Check if next line is a separator row (has dashes, starts with pipe)
      const looksLikeSeparator =
        nextLine.startsWith('|') && nextLine.includes('-')

      // Count columns in the separator row (if it looks like one)
      const separatorColCount = looksLikeSeparator
        ? (nextLine.match(/\|/g) || []).length - 1
        : 0

      // A separator is "complete" when:
      // 1. Ends with pipe
      // 2. Has same column count as header
      // 3. Each cell has proper separator format with spaces: " --- " or " :---: " etc.
      //    This ensures "|--- |" is replaced with "| --- |" (proper spacing)
      const hasProperSeparatorFormat =
        looksLikeSeparator &&
        nextLine.endsWith('|') &&
        /^\|( :?-{3,}:? \|)+$/.test(nextLine)

      const isCompleteSeparator =
        hasProperSeparatorFormat && separatorColCount === colCount

      if (isCompleteSeparator) {
        // LLM's separator is complete with matching columns - table is valid, nothing to do
      } else if (colCount > 0) {
        // Header is complete but separator is missing or incomplete
        // Add/replace with our synthetic separator
        const separator = '|' + ' --- |'.repeat(colCount)

        if (hasNewlineAfterHeader) {
          if (looksLikeSeparator || /^[\|\s:\-]*$/.test(nextLine)) {
            // LLM is typing separator (has dashes, or only separator chars like "|")
            // REPLACE their partial with our complete one
            lines[firstTableRowIdx + 1] = separator
          } else if (nextLine === '') {
            // Empty line after header - insert separator
            lines.splice(firstTableRowIdx + 1, 0, separator)
          } else {
            // Has actual content (data row) - insert separator before it
            lines.splice(firstTableRowIdx + 1, 0, separator)
          }
        } else {
          // No newline yet - append newline + separator directly to header line
          lines[firstTableRowIdx] = headerLine + '\n' + separator
        }
        result = lines.join('\n')
      }
    }
  }

  // SAFEGUARD: Validate that syntax is balanced after our closing attempt.
  // If we missed any edge cases, force-close the remaining markers.
  // This prevents cascade errors where block N's unclosed syntax affects block N+1.
  //
  // BUT: Only apply safeguard if there's actual content to format.
  // If the text is just markers (e.g., "**" or "`"), the original logic
  // intentionally leaves them open for the typing animation effect.
  // We detect "has content" by checking for non-marker, non-whitespace characters.
  const hasActualContent = /[^\s*_`~|]/.test(result)
  if (hasActualContent) {
    const validation = validateSyntaxBalance(result)
    if (!validation.isBalanced) {
      result = forceCloseSyntax(result, validation.unbalanced)
    }
  }

  return result
}

/**
 * Narrows a marked token to a list. The Token union includes Tokens.Generic
 * (`type: string`), so a type guard is required for property access.
 */
export const isListToken = (token: Token): token is Tokens.List =>
  token.type === 'list'

/**
 * Largest prefix of a streaming list token, in source chars from the list's
 * start, that can commit at top-level item boundaries: only fully revealed
 * items (their source ends at or before `revealedLength`), and never the
 * last item, which may still be growing. Item raws tile the list raw from
 * its start (the last item is the only one lexed without its trailing
 * newline); each boundary is verified before use so a lexer edge case
 * degrades to "no split", never to corrupted output.
 */
export function revealedListItemsPrefixLength(
  list: Tokens.List,
  revealedLength: number,
): number {
  let prefixLength = 0
  for (let index = 0; index < list.items.length - 1; index++) {
    const itemRaw = list.items[index].raw
    const end = prefixLength + itemRaw.length
    if (end > revealedLength) break
    if (!list.raw.startsWith(itemRaw, prefixLength)) return 0
    prefixLength = end
  }
  return prefixLength
}
